import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ListingScoreResponse } from 'dashboard/components/visuals-dashboard/widgets/listing-score-widget/listing-score-widget-data-response.interface';
import { IcePieRangesToDashboardPieRangesColorMap } from 'dashboard/constants/visuals-dashboard/ice-ranges-to-dashboard-ranges-colors-map';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import {
    IceVisualsDashboardPieChartComponent,
    PieChartCenterLabel,
    PieChartSegment,
    PieChartVariantEnum,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-pie-chart/ice-visuals-dashboard-pie-chart.component';

@Component({
    selector: 'app-listing-score-widget',
    imports: [IceVisualsDashboardPieChartComponent, TranslateModule],
    templateUrl: './listing-score-widget.component.html',
    styleUrl: './listing-score-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class ListingScoreWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    pieChartVariantEnum = PieChartVariantEnum;
    onLangChange = toSignal(this.translate.onLangChange);

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    listingScoreCenterLabel = computed(() => {
        const data = this.dataService.data();
        this.onLangChange(); // Re-compute when language changes
        const returnValue: PieChartCenterLabel = {
            title: this.translate.instant('dashboard.visuals.components.scoreDisplay.yourScore'),
            value: data ? data.summaryScore : 0,
            subtitle: this.translate.instant('dashboard.visuals.components.scoreDisplay.outOfTotal', { totalScore: 100 }),
        };
        return returnValue;
    });

    listingScoreSegments = computed(() => {
        const data = this.dataService.data();
        if (!data) {
            return [];
        }

        const segments: PieChartSegment[] = data.ranges.map((range) => {
            const mappedColor =
                IcePieRangesToDashboardPieRangesColorMap[range.colorCode as keyof typeof IcePieRangesToDashboardPieRangesColorMap];
            return {
                label: range.title,
                value: range.displayValue,
                color: mappedColor || range.colorCode,
            };
        });
        return segments;
    });

    constructor(
        public dataService: VisualsDashboardDataService<ListingScoreResponse>,
        private translate: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
    }
}
